import {useCallback, useEffect, useState} from 'react';

import {useCountryList} from '@/context/CountryListContext';
import useFilters from '@/hooks/useFilters';
import type {
  BlockedPaymentMethod,
  BlockedPaymentMethodFilters,
} from '@/services/blockedPaymentMethodService';
import {blockedPaymentMethodService} from '@/services/blockedPaymentMethodService';
import type {AfriexQueryParams} from '@/types';
import {CountryStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const useBlockedPaymentMethod = () => {
  const [list, setList] = useState<BlockedPaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [error, setError] = useState(null);
  const [shouldShowForm, setShouldShowForm] = useState<boolean>(false);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const handleApplyFilters = useCallback(() => {
    applyFilters(filtersToApply); // Apply the filters
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply]);
  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );
  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );
  const clearFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  countryList?.sort((a, b) => a?.name?.localeCompare(b.name));
  const fetchList = useCallback(
    async (params: BlockedPaymentMethodFilters) => {
      try {
        setIsLoading(true);
        const {
          data: items,
          total,
          limit,
          page,
        } = await blockedPaymentMethodService.getList({
          ...params,
          ...currentFilters,
        });
        setList(items);
        setTotal(total);
        setLimit(limit as number);
        setPage(page);
      } catch (e) {
        showErrorMessage(
          `Fetching blocked payment method list failed:${getErrorMessage(e, String(e))}`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  useEffect(() => {
    // Fire-and-forget: fetchList catches and surfaces its own failures, so this
    // only stops an unexpected throw becoming an unhandled rejection.
    fetchList({} as BlockedPaymentMethodFilters).catch(() => {});
  }, [fetchList]);

  const handleAddItem = useCallback(async (body: BlockedPaymentMethod) => {
    try {
      if (!body.country || !body.accountIdentifier || !body.type) {
        throw new Error('Please fill all required fields');
      }
      setError(null);
      setIsLoading(true);
      const blockedPaymentMethod =
        await blockedPaymentMethodService.saveOne(body);
      if (blockedPaymentMethod) {
        showSuccessMessage('Payment method blocked successfully');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (e) {
      showErrorMessage(
        `Adding blocked payment method failed:${getErrorMessage(e, String(e))}`,
      );
    }
    setIsLoading(false);
  }, []);

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm) {
        setIsSearch(false);
        setCurrentFilters({});
        await fetchList({} as BlockedPaymentMethodFilters);
        return;
      }
      setIsSearch(true);
      setCurrentFilters({
        accountIdentifier: searchTerm?.trim()?.toString(),
      } as BlockedPaymentMethodFilters);
      setIsPageReset(true);
    },
    [fetchList],
  );

  const handleRemoveItem = useCallback(async (id: string) => {
    try {
      if (!id) {
        throw new Error('No id provided');
      }
      setError(null);
      setIsLoading(true);
      await blockedPaymentMethodService.removeOne(id);

      showSuccessMessage('Payment method unblocked successfully');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      showErrorMessage(
        `Unblocking payment method failed:${getErrorMessage(e, String(e))}`,
      );
    }
    setIsLoading(false);
  }, []);

  const toggleForm = useCallback(() => {
    setShouldShowForm(!shouldShowForm);
  }, [shouldShowForm]);

  return {
    error,
    isLoading,
    isSearch,
    handleAddItem,
    handleSearch,
    handleRemoveItem,
    fetchList,
    pageInfo: {
      page,
      total,
      limit,
    },
    setPage,
    countryList: countryList?.filter((c) => c.status === CountryStatus.ACTIVE),
    list,
    shouldShowForm,
    toggleForm,
    currentFilters, // automatically triggers fetch
    filtersToApply, // requires handleApplyFilters to trigger fetch
    filterCount,
    isPageReset,
    handleApplyFilters,
    selectCurrentFilters,
    selectAppliedFilters,
    clearFilters,
  };
};

export default useBlockedPaymentMethod;
